import React from 'react';
import { ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { NavigationItem } from './mobileNavConfig';

type ColorScheme = 'warning' | 'amber';

const colorClasses: Record<
  ColorScheme,
  { active: string; activeIcon: string; hover: string; hoverIcon: string }
> = {
  warning: {
    active:
      'bg-warning-50 dark:bg-warning-950 text-warning-700 dark:text-warning-300 border border-warning-200 dark:border-warning-800',
    activeIcon: 'text-warning-600 dark:text-warning-400',
    hover:
      'hover:bg-neutral-50 dark:hover:bg-neutral-800 text-neutral-700 dark:text-neutral-300 hover:text-warning-600 dark:hover:text-warning-400',
    hoverIcon: 'text-neutral-400 dark:text-neutral-500 group-hover:text-warning-500',
  },
  amber: {
    active:
      'bg-amber-50 dark:bg-amber-950 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-800',
    activeIcon: 'text-amber-600 dark:text-amber-400',
    hover:
      'hover:bg-neutral-50 dark:hover:bg-neutral-800 text-neutral-700 dark:text-neutral-300 hover:text-amber-600 dark:hover:text-amber-400',
    hoverIcon: 'text-neutral-400 dark:text-neutral-500 group-hover:text-amber-500',
  },
};

export interface MobileNavItemProps {
  item: NavigationItem;
  isActive: boolean;
  onClick: () => void;
  colorScheme?: ColorScheme;
  badge?: React.ReactNode;
}

export const MobileNavItem: React.FC<MobileNavItemProps> = ({
  item,
  isActive,
  onClick,
  colorScheme = 'warning',
  badge,
}) => {
  const colors = colorClasses[colorScheme];

  return (
    <button
      onClick={onClick}
      className={cn(
        'w-full flex items-center justify-between p-3 rounded-lg text-left',
        'transition-colors duration-200 group',
        'focus:outline-none focus:ring-2 focus:ring-warning-500 focus:ring-offset-2 dark:focus:ring-offset-neutral-900',
        isActive ? colors.active : colors.hover
      )}
      aria-current={isActive ? 'page' : undefined}
    >
      <div className="flex items-center space-x-3">
        <div className={cn('flex-shrink-0', isActive ? colors.activeIcon : colors.hoverIcon)}>
          {item.icon}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">{item.label}</span>
            {badge}
          </div>
          {item.description && (
            <div className="text-xs text-helper group-hover:text-muted-foreground">
              {item.description}
            </div>
          )}
        </div>
      </div>
      <ChevronRight
        className={cn(
          'w-4 h-4 transition-transform duration-200',
          'group-hover:translate-x-1',
          isActive ? colors.activeIcon : 'text-neutral-400 dark:text-neutral-500'
        )}
      />
    </button>
  );
};
